/**
 * d3utility.js
 * Pure utility functions and constants shared by the D3 StepGraph component.
 * Nothing here imports React or touches D3 selections/scales directly.
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { getTimezoneLabel } from "../../utils/utility";
import { CHART_CONSTANTS } from "../../utils/constants";
import { CHART_PALETTE } from "../../constants/chartPalette";

dayjs.extend(utc);
dayjs.extend(tz);

// ── Visual constants ────────────────────────────
export const MARGIN = { top: 10, right: 20, bottom: 35, left: 40 };
export const COLOR_LINE = CHART_PALETTE.green;
export const COLOR_FILL_TOP = `${CHART_PALETTE.green}cc`;
export const COLOR_FILL_BOTTOM = `${CHART_PALETTE.green}4d`;

// ── Zoom constraints ──────────────────────────────────────────────────────────
/** Minimum visible time window in milliseconds (5-second precision). */
export const MIN_ZOOM_MS = CHART_CONSTANTS.MIN_ZOOM_WINDOW_MS;

// ── Chart instance ID ─────────────────────────────────────────────────────────
let _chartCounter = 0;
/**
 * Returns a new unique string ID for each chart instance.
 * Used to namespace SVG defs (clip-path, gradient) so multiple charts on the
 * same page never share the same id attribute.
 */
export function nextChartId() {
  return `stepgraph-${++_chartCounter}`;
}

// ── Data preparation ──────────────────────────────────────────────────────────
/**
 * Maps raw API data to the internal `{ x, y, rsn }` shape expected by D3,
 * then pins the first point to `dateRange[0]` and appends a closing point at
 * `dateRange[1]` when a dateRange is provided.
 *
 * @param {Array<{ts: number, s: number, rsn?: string}>} data
 * @param {[number|Date, number|Date]|undefined} dateRange
 * @returns {Array<{x: number, y: number, rsn: string}>}
 */
export function prepareChartData(data, dateRange) {
  const chartData = data.map(item => ({ x: item.ts, y: item.s, rsn: item.rsn }));

  if (dateRange?.length === 2 && chartData.length > 0) {
    const toEpoch = val => {
      if (typeof val === "number") return val;
      const d = new Date(val);
      return isNaN(d.getTime()) ? val : d.getTime();
    };
    const startEpoch = toEpoch(dateRange[0]);
    const endEpoch = toEpoch(dateRange[1]);
    chartData[0] = { ...chartData[0], x: startEpoch };
    if (chartData[chartData.length - 1].x !== endEpoch) {
      chartData.push({ x: endEpoch, y: chartData[chartData.length - 1].y, rsn: "" });
    }
  }

  return chartData;
}

// ── Zoom clamping ─────────────────────────────────────────────────────────────
/**
 * Enforces a minimum visible time window after a brush-zoom selection.
 * If the selected [tMin, tMax] span is narrower than `minMs`, the window is
 * expanded symmetrically around its midpoint and then clamped to the original
 * data domain so it never exceeds the data boundaries.
 *
 * @param {number} tMin        - Selected start epoch ms.
 * @param {number} tMax        - Selected end epoch ms.
 * @param {[number, number]} originalDomainMs - Full data domain [start, end] ms.
 * @param {number} [minMs]     - Minimum window width in ms (default MIN_ZOOM_MS).
 * @returns {[number, number]} - Clamped [tMin, tMax].
 */
export function clampZoomDomain(tMin, tMax, originalDomainMs, minMs = MIN_ZOOM_MS) {
  if (tMax - tMin < minMs) {
    const mid = (tMin + tMax) / 2;
    tMin = mid - minMs / 2;
    tMax = mid + minMs / 2;
    if (tMin < originalDomainMs[0]) {
      tMin = originalDomainMs[0];
      tMax = tMin + minMs;
    }
    if (tMax > originalDomainMs[1]) {
      tMax = originalDomainMs[1];
      tMin = tMax - minMs;
    }
  }
  return [tMin, tMax];
}

// ── Export: data formatting ───────────────────────────────────────────────────
/**
 * Formats an epoch timestamp as "YYYY-MM-DD HH:MM:SS" (matching
 * LineBarComposedChart_new format for Excel compatibility).
 *
 * @param {number} epochMs
 * @returns {string}
 */
export function fmtExportDate(epochMs, siteTimezone) {
  const d = siteTimezone ? dayjs(epochMs).tz(siteTimezone) : dayjs(epochMs);
  return d.format("YYYY-MM-DD HH:mm:ss");
}

/**
 * Builds export content string with specified delimiter (matches LineBarComposedChart_new pattern).
 *
 * @param {Array<{x: number, y: number, rsn?: string}>} chartData
 * @param {string} delimiter - "," for CSV, "\t" for XLS
 * @returns {string}
 */
function buildExportContent(chartData, delimiter, siteTimezone) {
  const headers = [`Timestamp (${getTimezoneLabel(siteTimezone)})`, "Status", "Reason"];
  const rows = chartData.map(d => {
    const timestamp = fmtExportDate(d.x, siteTimezone);
    const status = d.y === 1 ? "Online" : "Offline";
    const reason = d.rsn || "";
    return [timestamp, status, reason].join(delimiter);
  });
  return [headers.join(delimiter), ...rows].join("\n");
}

// ── Export: file downloads ────────────────────────────────────────────────────
/**
 * Generates CSV content and triggers download.
 *
 * @param {Array<{x: number, y: number, rsn?: string}>} chartData
 * @param {string} [filename]
 */
export function downloadCSVFile(chartData, filename = CHART_CONSTANTS.EXPORT_FILENAME_CSV, siteTimezone) {
  const content = buildExportContent(chartData, ",", siteTimezone);
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/**
 * Generates XLS (TSV) content and triggers download (matches LineBarComposedChart_new exactly).
 *
 * @param {Array<{x: number, y: number, rsn?: string}>} chartData
 * @param {string} [filename]
 */
export function downloadXLSFile(chartData, filename = CHART_CONSTANTS.EXPORT_FILENAME_XLSX, siteTimezone) {
  const content = buildExportContent(chartData, "\t", siteTimezone);
  const blob = new Blob([content], { type: "application/vnd.ms-excel" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/**
 * Renders an SVG element onto a Canvas and triggers a PNG download.
 * White background is painted first so transparent regions export cleanly.
 *
 * @param {SVGElement} svgEl
 * @param {string} [filename]
 */
export function exportSvgToPng(svgEl, filename = CHART_CONSTANTS.EXPORT_FILENAME_PNG) {
  const w = +svgEl.getAttribute("width") || svgEl.clientWidth;
  const h = +svgEl.getAttribute("height") || svgEl.clientHeight;
  const svgStr = new XMLSerializer().serializeToString(svgEl);
  const blob = new Blob([svgStr], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0);
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  img.src = url;
}

// ── Fullscreen ────────────────────────────────────────────────────────────────
/**
 * Toggles browser fullscreen for the given element.
 *
 * @param {HTMLElement} el
 */
export function toggleFullscreen(el) {
  if (!document.fullscreenElement && !el) return;

  if (document.fullscreenElement) {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  } else {
    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (el.mozRequestFullScreen) {
      el.mozRequestFullScreen();
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen();
    }
  }
}
